import React, { useEffect, useRef } from 'react';

const DeviceModal = ({ isOpen, onClose, deviceType }) => {
  const modalRef = useRef(null);
  
  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  
  // Define content for each device type
  const getContent = () => {
    switch(deviceType) {
      case 'basic':
        return (
          <>
            <h2 className="modal-title">AeroNode Basic: Your Entry Point to AeroFi's RWA Mining Ecosystem</h2>
            
            <h3>Introduction to the AeroNode Basic Package</h3>
            <p>The AeroNode Basic represents AeroFi's entry-level Real World Asset (RWA) device package, available at 499 USDT. This affordable mining node is designed for beginners looking to participate in the growing Web3 RWA ecosystem without significant initial investment.</p>
            
            <h3>What Is a Real World Asset (RWA) Package?</h3>
            <p>AeroFi RWA packages are physical WiFi devices that serve a dual purpose:</p>
            <ol>
              <li><strong>Practical Utility:</strong> Providing reliable internet connectivity with real-world application</li>
              <li><strong>Passive Income Generation:</strong> Mining AFI tokens through the AeroFi blockchain ecosystem</li>
            </ol>
            <p>Unlike traditional crypto mining that requires specialized hardware and enormous energy consumption, AeroNode devices utilize AI-enhanced connectivity technology to generate token rewards while serving a practical purpose.</p>
            
            <h3>AeroNode Basic: Technical Specifications</h3>
            <p>The AeroNode Basic offers a solid foundation for new participants with the following specifications:</p>
            <ul>
              <li><strong>Mining Factor:</strong> 1.0× (base level)</li>
              <li><strong>Hardware Configuration:</strong> Single-unit device</li>
              <li><strong>Connectivity:</strong> Entry-level WiFi capabilities</li>
              <li><strong>Uptime Requirement:</strong> Minimum 85% uptime for full rewards</li>
              <li><strong>Registration Fee:</strong> 500 AFI (automatically deducted from initial mining rewards)</li>
            </ul>
            
            <h3>Reward Structure and APY</h3>
            <p>The AeroNode Basic offers a flexible staking structure that allows you to maximize your returns based on your level of commitment:</p>
            
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Staking Tier</th>
                  <th>AFI Tokens Staked</th>
                  <th>APY (Annual Percentage Yield)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Base Tier</td>
                  <td>No staking required</td>
                  <td>30% APY</td>
                </tr>
                <tr>
                  <td>Mid Tier</td>
                  <td>5,000 AFI</td>
                  <td>48% APY</td>
                </tr>
                <tr>
                  <td>High Tier</td>
                  <td>10,000 AFI</td>
                  <td>69% APY</td>
                </tr>
              </tbody>
            </table>
            
            <p className="note"><strong>Important Note:</strong> All APY figures are token-denominated, meaning they represent returns in AFI tokens, not USD value. The actual USD value of returns will fluctuate with the market price of the AFI token.</p>
            
            <h3>Early Adopter Advantage</h3>
            <p>As with all AeroFi devices, the AeroNode Basic benefits from the network's Temporal Reward Adjustment mechanism, which provides:</p>
            <ul>
              <li><strong>Early Adopter Bonus:</strong> +50% higher rewards for early participants</li>
              <li><strong>Permanent Advantage:</strong> Your position in the ecosystem is permanently recorded on-chain and reflected in your ongoing rewards</li>
              <li><strong>Long-term Benefit:</strong> Early adoption advantage remains even as the network grows</li>
            </ul>
            
            <h3>Comparison with Higher-Tier Devices</h3>
            <p>The AeroNode Basic has several distinctions when compared to more expensive models:</p>
            <ul>
              <li><strong>No staking requirement to begin:</strong> Unlike higher-tier models, you can start earning with the Basic model without any initial staking</li>
              <li><strong>Lower entry price:</strong> At 499 USDT, it costs significantly less than the Pro (999 USDT), Elite (1,999 USDT), or Enterprise (4,999 USDT) models</li>
              <li><strong>Lower mining factor:</strong> The 1.0× mining factor is lower than other models (Pro: 2.0×, Elite: 3.0×, Enterprise: 6.0×)</li>
              <li><strong>No network saturation protection:</strong> Higher-tier devices receive priority when the network approaches maximum emission capacity</li>
            </ul>
            
            <h3>First-Year Return Projection</h3>
            <p>Based on current protocol parameters and assuming sufficient network revenue, an AeroNode Basic with mid-level staking (5,000 AFI) could potentially yield approximately 24,950 AFI tokens in the first year.</p>
            
            <h3>Who Should Choose the AeroNode Basic?</h3>
            <p>The AeroNode Basic is ideal for:</p>
            <ul>
              <li><strong>Beginners:</strong> Those new to RWA mining who want to test the ecosystem with minimal investment</li>
              <li><strong>Budget-conscious investors:</strong> Individuals looking for the lowest entry point into token-generating physical assets</li>
              <li><strong>Network participants:</strong> Users who benefit from both the connectivity utility and mining capabilities</li>
              <li><strong>Those building a portfolio:</strong> Investors looking to start with a basic node before expanding to higher tiers</li>
            </ul>
            
            <h3>Getting Started with Your AeroNode Basic</h3>
            <ol>
              <li><strong>Purchase:</strong> Acquire your AeroNode Basic for 499 USDT through the official AeroFi platform</li>
              <li><strong>Setup:</strong> Follow the simple plug-and-play installation instructions</li>
              <li><strong>Registration:</strong> Complete device registration (500 AFI fee automatically deducted from mining rewards)</li>
              <li><strong>Optional Staking:</strong> Consider staking AFI tokens to increase your APY (up to 69%)</li>
              <li><strong>Dashboard Access:</strong> Monitor your device performance, uptime, and rewards through the AeroFi dashboard</li>
            </ol>
            
            <p className="note">All projections and APY figures are token-denominated and subject to protocol revenue, network conditions, and token market price fluctuations.</p>
          </>
        );
      
      case 'pro':
        return (
          <>
            <h2 className="modal-title">AeroNode Pro: Enhanced Performance and Returns for Mid-Tier RWA Mining</h2>
            
            <h3>AeroNode Pro Overview</h3>
            <p>The AeroNode Pro is AeroFi's mid-tier mining solution, available at 999 USDT, designed for users seeking higher returns and stronger performance. This device balances practicality with profitability and is the ideal choice for both regular and advanced miners.</p>
            
            <h3>Technical Specifications and Features</h3>
            <p>The AeroNode Pro offers the following key features:</p>
            <ul>
              <li><strong>Mining Factor:</strong> 2.0× (twice the efficiency of the basic model)</li>
              <li><strong>Hardware Configuration:</strong> Single high-performance device</li>
              <li><strong>Connectivity:</strong> Enhanced signal strength and broader coverage</li>
              <li><strong>Uptime Requirement:</strong> Minimum 85% uptime for full rewards</li>
              <li><strong>Registration Fee:</strong> 400 AFI (lower than basic model, automatically deducted from initial mining rewards)</li>
              <li><strong>Special Features:</strong> Basic network saturation protection, providing priority when the network approaches maximum emission capacity</li>
            </ul>
            
            <h3>Staking Structure and APY</h3>
            <p>The AeroNode Pro requires initial staking to activate and offers the following tiered staking options:</p>
            
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Staking Tier</th>
                  <th>AFI Tokens Staked</th>
                  <th>APY (Annual Percentage Yield)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Base Tier</td>
                  <td>8,000 AFI</td>
                  <td>40% APY</td>
                </tr>
                <tr>
                  <td>Mid Tier</td>
                  <td>15,000 AFI</td>
                  <td>120% APY</td>
                </tr>
                <tr>
                  <td>High Tier</td>
                  <td>25,000 AFI</td>
                  <td>180% APY</td>
                </tr>
              </tbody>
            </table>
            
            <h3>Comparison with Other Devices</h3>
            <p>Compared to the AeroNode Basic, the Pro model offers:</p>
            <ul>
              <li>2× the mining efficiency</li>
              <li>Higher potential APY (up to 180% vs 69% for Basic)</li>
              <li>Basic network saturation protection</li>
              <li>Lower registration fee (400 AFI vs 500 AFI for Basic)</li>
            </ul>
            <p>Compared to higher-tier models, the Pro strikes a balance between price and performance, offering the best value for money.</p>
            
            <h3>First-Year Return Projection</h3>
            <p>Based on current protocol parameters and assuming sufficient network revenue, an AeroNode Pro with mid-level staking (15,000 AFI) could potentially yield approximately 89,910 AFI tokens in the first year.</p>
            
            <h3>Ideal Users</h3>
            <p>The AeroNode Pro is particularly suited for:</p>
            <ul>
              <li><strong>Experienced miners:</strong> Users who understand the basics and want to increase their mining efficiency</li>
              <li><strong>Medium-budget investors:</strong> Those seeking higher returns without investing in the highest-tier devices</li>
              <li><strong>Long-term participants:</strong> Users planning to stay in the AeroFi ecosystem long-term and benefit from early adopter advantages</li>
              <li><strong>Balance seekers:</strong> Participants looking for a balance between investment cost and potential returns</li>
            </ul>
            
            <h3>Getting Started with Your AeroNode Pro</h3>
            <ol>
              <li><strong>Purchase:</strong> Acquire your AeroNode Pro for 999 USDT through the official AeroFi platform</li>
              <li><strong>Setup:</strong> Follow the simple plug-and-play installation instructions</li>
              <li><strong>Staking:</strong> Stake at least 8,000 AFI to activate the device and begin mining</li>
              <li><strong>Registration:</strong> Complete device registration (400 AFI fee automatically deducted from mining rewards)</li>
              <li><strong>Optimization:</strong> Consider increasing your stake to higher tiers to increase your APY (up to 180%)</li>
            </ol>
            
            <h3>Early Adopter Advantage</h3>
            <p>As part of the AeroFi network, AeroNode Pro users enjoy:</p>
            <ul>
              <li><strong>Early adopter rewards:</strong> 50% higher rewards than later participants</li>
              <li><strong>Permanent advantage:</strong> Your position in the ecosystem is permanently recorded on-chain and reflected in ongoing rewards</li>
              <li><strong>Time value:</strong> The earlier you join, the higher weight in the temporal coefficient</li>
            </ul>
            
            <p className="note">All APY figures are token-denominated, and the actual USD value will fluctuate with the market price of the AFI token.</p>
          </>
        );
        
      case 'elite':
        return (
          <>
            <h2 className="modal-title">AeroNode Elite: Premium Mining Experience with Superior Returns</h2>
            
            <h3>AeroNode Elite Overview</h3>
            <p>The AeroNode Elite represents a premium solution in the AeroFi ecosystem, available at 1,999 USDT, designed for professional users seeking high performance and exceptional returns. This device combines cutting-edge technology with enhanced mining capabilities.</p>
            
            <h3>Technical Specifications and Features</h3>
            <p>The AeroNode Elite boasts the following advanced features:</p>
            <ul>
              <li><strong>Mining Factor:</strong> 3.0× (three times the efficiency of the basic model)</li>
              <li><strong>Hardware Configuration:</strong> Single premium device with superior components</li>
              <li><strong>Technical Highlights:</strong> Advanced AI optimization algorithms ensuring optimal performance</li>
              <li><strong>Connectivity:</strong> Extended connection range covering larger areas</li>
              <li><strong>Uptime Requirement:</strong> Minimum 85% uptime for full rewards</li>
              <li><strong>Registration Fee:</strong> 300 AFI (automatically deducted from initial mining rewards)</li>
              <li><strong>Special Features:</strong> Advanced network saturation protection, providing higher priority</li>
            </ul>
            
            <h3>Staking Structure and APY</h3>
            <p>The AeroNode Elite requires initial staking to activate and offers the following tiered staking options:</p>
            
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Staking Tier</th>
                  <th>AFI Tokens Staked</th>
                  <th>APY (Annual Percentage Yield)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Base Tier</td>
                  <td>20,000 AFI</td>
                  <td>50% APY</td>
                </tr>
                <tr>
                  <td>Mid Tier</td>
                  <td>35,000 AFI</td>
                  <td>250% APY</td>
                </tr>
                <tr>
                  <td>High Tier</td>
                  <td>60,000 AFI</td>
                  <td>350% APY</td>
                </tr>
              </tbody>
            </table>
            
            <h3>Differential Advantages</h3>
            <p>The AeroNode Elite offers significant advantages over lower-tier models:</p>
            <ul>
              <li>3× the mining efficiency of the basic model</li>
              <li>Significantly higher maximum APY potential (up to 350%)</li>
              <li>Advanced network saturation protection</li>
              <li>Lower registration fee (300 AFI)</li>
              <li>Advanced AI optimization ensuring optimal performance</li>
            </ul>
            
            <h3>First-Year Return Projection</h3>
            <p>Based on current protocol parameters and assuming sufficient network revenue, an AeroNode Elite with mid-level staking (35,000 AFI) could potentially yield approximately 349,825 AFI tokens in the first year.</p>
            
            <h3>Ideal User Profile</h3>
            <p>The AeroNode Elite is particularly suited for:</p>
            <ul>
              <li><strong>Professional miners:</strong> Seasoned participants seeking quality performance and high returns</li>
              <li><strong>Medium-large investors:</strong> Users with sufficient capital to invest in premium equipment and larger staking amounts</li>
              <li><strong>Technology enthusiasts:</strong> Those who appreciate cutting-edge AI optimization and premium hardware</li>
              <li><strong>Long-term investors:</strong> Users planning to participate in the AeroFi ecosystem long-term and maximize returns</li>
            </ul>
            
            <h3>Getting Started with Your AeroNode Elite</h3>
            <ol>
              <li><strong>Purchase:</strong> Acquire your AeroNode Elite for 1,999 USDT through the official AeroFi platform</li>
              <li><strong>Setup:</strong> Follow the advanced setup guide, optimizing device placement for best connectivity</li>
              <li><strong>Staking:</strong> Stake at least 20,000 AFI to activate the device and begin mining</li>
              <li><strong>Monitoring:</strong> Utilize the advanced dashboard features to track device performance, network status, and rewards</li>
              <li><strong>Optimization:</strong> Consider increasing your stake to mid or high levels to significantly boost APY (up to 350%)</li>
            </ol>
            
            <h3>Early Adopter Advantage</h3>
            <p>Like all AeroFi devices, the Elite model benefits from the network's temporal reward adjustment mechanism:</p>
            <ul>
              <li><strong>50% additional rewards:</strong> Early participants receive higher returns</li>
              <li><strong>Permanent advantage:</strong> Your time of entry is recorded on-chain, affecting long-term reward rates</li>
              <li><strong>Priority network protection:</strong> Enjoy higher priority during network peak loads</li>
            </ul>
            
            <p className="note">All return projections are based on token quantity, not dollar value, and actual returns will be affected by protocol revenue and market conditions.</p>
          </>
        );
        
      case 'enterprise':
        return (
          <>
            <h2 className="modal-title">AeroNode Enterprise: Flagship RWA Mining Cluster System</h2>
            
            <h3>AeroNode Enterprise Overview</h3>
            <p>The AeroNode Enterprise is the pinnacle solution in the AeroFi ecosystem, available at 4,999 USDT, specifically designed for institutional-grade investors and professional mining operators. This flagship product represents the peak of RWA mining technology, offering unparalleled performance and return potential.</p>
            
            <h3>Technical Specifications and Breakthrough Features</h3>
            <p>The AeroNode Enterprise boasts the following exceptional features:</p>
            <ul>
              <li><strong>Mining Factor:</strong> 6.0× (six times the efficiency of the basic model)</li>
              <li><strong>Hardware Configuration:</strong> 8-device hardware cluster working in unison to maximize performance</li>
              <li><strong>Connection Technology:</strong> Enterprise-grade network infrastructure providing maximum coverage and stability</li>
              <li><strong>Uptime Requirement:</strong> Minimum 85% uptime for full rewards</li>
              <li><strong>Registration Fee:</strong> Only 200 AFI (lowest of all models, automatically deducted from initial mining rewards)</li>
              <li><strong>Exclusive Features:</strong> Priority network saturation protection, ensuring maximum reward allocation even during network peaks</li>
            </ul>
            
            <h3>Elite Staking Structure and Ultra-High APY</h3>
            <p>The AeroNode Enterprise requires initial staking to activate and offers the following tiered staking options:</p>
            
            <table className="modal-table">
              <thead>
                <tr>
                  <th>Staking Tier</th>
                  <th>AFI Tokens Staked</th>
                  <th>APY (Annual Percentage Yield)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Base Tier</td>
                  <td>45,000 AFI</td>
                  <td>60% APY</td>
                </tr>
                <tr>
                  <td>Mid Tier</td>
                  <td>75,000 AFI</td>
                  <td>400% APY</td>
                </tr>
                <tr>
                  <td>High Tier</td>
                  <td>160,000 AFI</td>
                  <td>600% APY</td>
                </tr>
              </tbody>
            </table>
            
            <h3>Unparalleled Advantages</h3>
            <p>The AeroNode Enterprise offers overwhelming advantages compared to all other models:</p>
            <ul>
              <li>6× the mining efficiency of the basic model (highest in the series)</li>
              <li>8-device cluster system providing maximum mining capability</li>
              <li>Industry-leading highest APY potential (up to 600%)</li>
              <li>Priority network saturation protection, ensuring maximum rewards under any conditions</li>
              <li>Lowest registration fee (200 AFI), saving initial costs</li>
            </ul>
            
            <h3>First-Year Return Potential</h3>
            <p>Based on current protocol parameters and assuming sufficient network revenue, an AeroNode Enterprise with mid-level staking (75,000 AFI) could potentially yield approximately 1,499,700 AFI tokens in the first year, unmatched in the AeroFi device series.</p>
            
            <h3>Designed for Elite Users</h3>
            <p>The AeroNode Enterprise is designed for:</p>
            <ul>
              <li><strong>Institutional investors:</strong> Organizations and companies looking to participate in RWA mining at scale</li>
              <li><strong>Professional mining operators:</strong> Users already managing multiple mining operations and seeking maximum efficiency</li>
              <li><strong>High net worth investors:</strong> Individuals capable of committing significant capital for maximum returns</li>
              <li><strong>Technology pioneers:</strong> Users wanting to own the most advanced RWA mining technology on the market</li>
            </ul>
            
            <h3>Deploying Your Enterprise-Grade Solution</h3>
            <ol>
              <li><strong>Purchase:</strong> Acquire your AeroNode Enterprise for 4,999 USDT through the official AeroFi platform</li>
              <li><strong>Professional Installation:</strong> Utilize the provided expert support for optimized deployment</li>
              <li><strong>Staking Strategy:</strong> Stake at least 45,000 AFI to activate the device, and consider higher-tier staking for exceptional returns</li>
              <li><strong>Performance Monitoring:</strong> Use the enterprise-grade dashboard to continuously monitor and optimize the performance of your 8-device cluster</li>
              <li><strong>Expansion Potential:</strong> Consider deploying multiple Enterprise units to create larger-scale mining infrastructure</li>
            </ol>
            
            <h3>Enterprise-Grade Early Adopter Advantage</h3>
            <p>As the flagship product, Enterprise users enjoy maximized early adopter benefits:</p>
            <ul>
              <li><strong>Temporal Advantage:</strong> Like all devices, receive the 50% early adopter bonus</li>
              <li><strong>Network Priority:</strong> Highest level of network saturation protection, ensuring optimal rewards under any conditions</li>
              <li><strong>Scale Effect:</strong> The 8-device cluster multiplies the benefits of early adopter rewards</li>
            </ul>
            
            <h3>Summary</h3>
            <p>The AeroNode Enterprise represents the ultimate investment opportunity in the AeroFi ecosystem. While the initial investment is higher, its exceptional efficiency, cluster design, and ultra-high APY potential make it the ideal choice for professional investors seeking maximum returns.</p>
            
            <p className="note">Important: APYs are token-denominated, not USD values. Actual returns may be reduced if network emission capacity or protocol revenue is insufficient.</p>
          </>
        );
        
      default:
        return <p>Select a device to see detailed information.</p>;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-content" ref={modalRef}>
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="modal-scroll-content">
            {getContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceModal;
